import { memo, useEffect, useMemo, useRef, useState } from 'react'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  type AgentTranscript,
  type DocumentTextPage,
  useDocumentText,
} from '~/entities/document-agent'
import { pluralize } from '~/shared/lib/pluralize'
import { IconButton, SkeletonBlock, Textfield } from '~/shared/ui'
import { findText, getTranscriptPages, paintHighlight } from '../../lib'
import { useDocumentPane } from '../../model'
import { MaterialIcon, Muted } from '../primitives'
import './document-text.css'

const QUOTE_HIGHLIGHT = 'agent-run-quote'
const FIND_HIGHLIGHT = 'agent-run-find'
const FIND_DEBOUNCE_MS = 200

/** Wide tables scroll sideways instead of breaking numbers mid-figure. */
const MARKDOWN_COMPONENTS: Components = {
  table: ({ children }) => (
    <div className="mb-3 overflow-x-auto">
      <table>{children}</table>
    </div>
  ),
}

function scrollToRange(range: Range | undefined) {
  const element = range?.startContainer.parentElement
  element?.scrollIntoView({ block: 'center', behavior: 'smooth' })
}

/** Pages render once per text; highlights are painted over them without re-rendering. */
const Pages = memo(function Pages({ pages }: { pages: DocumentTextPage[] }) {
  return (
    <>
      {pages.map(({ page, text }) => (
        <section
          key={page}
          data-text-page={page}
          className="flex scroll-mt-2 flex-col gap-2"
        >
          <span className="text-text-tertiary text-[11px] leading-4 font-medium tracking-[0.4px] uppercase">
            {`Page ${page}`}
          </span>
          <div className="agent-run-markdown border-divider bg-surface-lowest rounded-lg border px-5 py-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
            {text.trim() ? (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={MARKDOWN_COMPONENTS}
              >
                {text}
              </ReactMarkdown>
            ) : (
              <Muted className="italic">Blank page</Muted>
            )}
          </div>
        </section>
      ))}
    </>
  )
})

type QuoteState = { found: number; partial: boolean; isSearch: boolean } | null

export function TextView({
  documentKey,
  transcripts,
  isActive,
}: {
  documentKey: string
  transcripts: Array<AgentTranscript | null>
  isActive: boolean
}) {
  const { data, isPending, isError } = useDocumentText(documentKey)
  const { target } = useDocumentPane()
  const fallback = useMemo(() => getTranscriptPages(transcripts), [transcripts])
  const pages = useMemo(() => data?.pages ?? (isError ? fallback : []), [data, isError, fallback])
  const isFallback = !data && isError
  const contentRef = useRef<HTMLDivElement>(null)
  const [quote, setQuote] = useState<QuoteState>(null)
  const [query, setQuery] = useState('')
  const [findRanges, setFindRanges] = useState<Range[]>([])
  const [findIndex, setFindIndex] = useState(0)

  // A quote or page asked for from the review, handled once each.
  const handledTarget = useRef<number | null>(null)
  useEffect(() => {
    const root = contentRef.current
    if (!target || !root || !isActive || pages.length === 0) return
    if (handledTarget.current === target.id) return
    handledTarget.current = target.id
    if (target.kind === 'page') {
      root.querySelector(`[data-text-page="${target.page}"]`)?.scrollIntoView({ block: 'start' })
      return
    }
    const { ranges, partial } = findText(root, target.text)
    paintHighlight(QUOTE_HIGHLIGHT, ranges)
    setQuote({ found: ranges.length, partial, isSearch: target.isSearch })
    scrollToRange(ranges[0])
  }, [target, isActive, pages])

  // Find in the text, as you type.
  useEffect(() => {
    const root = contentRef.current
    if (!root) return
    const timer = setTimeout(() => {
      const { ranges } =
        query.trim().length >= 2 ? findText(root, query, { limit: 500 }) : { ranges: [] }
      paintHighlight(FIND_HIGHLIGHT, ranges)
      setFindRanges(ranges)
      setFindIndex(0)
      scrollToRange(ranges[0])
    }, FIND_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [query, pages])

  useEffect(
    () => () => {
      paintHighlight(QUOTE_HIGHLIGHT, [])
      paintHighlight(FIND_HIGHLIGHT, [])
    },
    [],
  )

  const clearQuote = () => {
    paintHighlight(QUOTE_HIGHLIGHT, [])
    setQuote(null)
  }

  const stepFind = (delta: number) => {
    if (findRanges.length === 0) return
    const next = (findIndex + delta + findRanges.length) % findRanges.length
    setFindIndex(next)
    scrollToRange(findRanges[next])
  }

  if (isPending) {
    return (
      <div className="flex flex-col gap-3 p-4">
        <SkeletonBlock className="h-6 w-40" />
        <SkeletonBlock className="h-64 w-full" />
        <SkeletonBlock className="h-64 w-full" />
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="border-divider flex shrink-0 flex-col gap-2 border-b px-4 py-2.5">
        <div className="flex items-center gap-2">
          <Textfield
            className="flex-1"
            placeholder="Find in the text"
            value={query}
            clearable={query.length > 0}
            materialClearIcon="close"
            onClear={() => setQuery('')}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') stepFind(event.shiftKey ? -1 : 1)
            }}
          />
          {query.trim().length >= 2 && (
            <>
              <Muted className="shrink-0 tabular-nums">
                {findRanges.length ? `${findIndex + 1} of ${findRanges.length}` : 'No matches'}
              </Muted>
              <IconButton
                materialIcon="keyboard_arrow_up"
                size="medium"
                container="small"
                disabled={findRanges.length === 0}
                onClick={() => stepFind(-1)}
              />
              <IconButton
                materialIcon="keyboard_arrow_down"
                size="medium"
                container="small"
                disabled={findRanges.length === 0}
                onClick={() => stepFind(1)}
              />
            </>
          )}
        </div>
        <Muted>
          {isFallback
            ? `The document text is not available, so this shows the ${pages.length} ${pluralize(pages.length, 'page')} the agent read, from its transcript.`
            : `The text the agent read: ${pages.length} ${pluralize(pages.length, 'page')}, from the document's OCR${data?.truncated ? ' (cut short: the text is very long)' : ''}.`}
        </Muted>
      </div>

      {quote && (
        <div className="bg-swatch-yellow-50 text-swatch-yellow-900 flex shrink-0 items-center gap-2 px-4 py-2 text-[12px] leading-4">
          <MaterialIcon
            name="format_quote"
            className="text-[16px]"
          />
          <span className="flex-1">
            {quote.isSearch
              ? quote.found
                ? `The agent's search, highlighted in ${quote.found} ${pluralize(quote.found, 'place')}.`
                : "The agent's search does not appear word for word in the text."
              : quote.found === 0
                ? 'This quote is not in the text word for word. The agent may have paraphrased it, or it is on a page shown only as an image.'
                : quote.partial
                  ? 'Only the opening words of this quote were found; the rest differs from the text.'
                  : `The agent's quote, highlighted${quote.found > 1 ? ` (${quote.found} places)` : ''}.`}
          </span>
          <button
            type="button"
            onClick={clearQuote}
            className="text-swatch-yellow-900 cursor-pointer border-none bg-transparent p-0 text-[12px] font-medium hover:underline"
          >
            Clear
          </button>
        </div>
      )}

      <div
        ref={contentRef}
        className="bg-surface-low min-h-0 flex-1 overflow-y-auto"
      >
        {pages.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
            <MaterialIcon
              name="text_snippet"
              className="text-text-tertiary text-[32px]"
            />
            <Muted className="text-[13px] leading-5">
              No text is available for this document. Documents from before 25 August 2026 have no
              stored text.
            </Muted>
          </div>
        ) : (
          <div className="flex flex-col gap-5 px-4 py-4">
            <Pages pages={pages} />
          </div>
        )}
      </div>
    </div>
  )
}
